import { type ReactNode, useLayoutEffect, useRef } from "react";

/** Fixed-height procedure list; reports overflow for the step header indicator. */
export function StepProcedureViewport({
  children,
  label,
  onOverflowChange,
}: {
  children: ReactNode;
  label: string;
  onOverflowChange: (overflow: boolean) => void;
}) {
  const viewport = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    const element = viewport.current;
    if (!element) return;
    const update = () => onOverflowChange(element.scrollHeight > element.clientHeight + 1);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    if (element.firstElementChild) observer.observe(element.firstElementChild);
    return () => observer.disconnect();
  }, [onOverflowChange]);
  return (
    <section
      ref={viewport}
      aria-label={label}
      // biome-ignore lint/a11y/noNoninteractiveTabindex: Scrollable region supports keyboard navigation.
      tabIndex={0}
      className="relative z-20 pointer-events-auto min-h-0 flex-1 overflow-y-auto scrollbar-none px-1 pb-1 focus-visible:outline-2 focus-visible:outline-primary"
    >
      {children}
    </section>
  );
}
